import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Box,
    Drawer,
    Grid,
    Typography,
    Divider,
    TextField,
    Checkbox,
    FormControlLabel,
} from "@mui/material";

import {
    useLazyGetTransferBookingDetailsQuery,
    useTransferBookingMutation,
} from "../../../store/booking/bookingApis";

import CloseIcon from "@mui/icons-material/CloseOutlined";

import IconButtonIcons from "../../Common/IconButtonIcons";
import Button from "../../Common/Button";
import AutoCompleteServerSide from "../../Common/AutoCompleteServerSide";
import { setSnackBar } from "../../../store/common/commonSlice";

export const EnrollActivityTransferDrawer = ({
    show,
    close,
    data,
    getActivityList,
}) => {
    const dispatch = useDispatch();

    const [getTransferBookingDetails] =
        useLazyGetTransferBookingDetailsQuery();

    const [transferBooking, { isLoading: transferLoading }] =
        useTransferBookingMutation();

    const [selectedActivity, setSelectedActivity] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedTransferMember, setSelectedTransferMember] = useState(null);
    const [transferToPrimary, setTransferToPrimary] = useState(false);

    const [paidAmount, setPaidAmount] = useState(0);
    const [additionalAmount, setAdditionalAmount] = useState(0);

    const [isPartialTransfer, setIsPartialTransfer] = useState(false);
    const [transferAmount, setTransferAmount] = useState(0);

    const [transferReason, setTransferReason] = useState("");
    const [transferNotes, setTransferNotes] = useState("");

    useEffect(() => {
        const fetchPaidAmount = async () => {
            if (!show || !data?._id) {
                return;
            }

            try {
                const response = await getTransferBookingDetails({
                    _id: data._id,
                }).unwrap();

                setPaidAmount(
                    Number(response?.actual_paid_amount || 0)
                );
            } catch (error) {
                console.error(
                    "Failed to fetch transfer details:",
                    error
                );

                setPaidAmount(0);
            }
        };

        fetchPaidAmount();
    }, [show, data?._id, getTransferBookingDetails]);

    const handleClose = () => {
        setSelectedActivity(null);
        setSelectedBatch(null);
        setSelectedPlan(null);
        setSelectedTransferMember(null);
        setTransferToPrimary(false);
        setPaidAmount(0);
        setAdditionalAmount(0);
        setIsPartialTransfer(false);
        setTransferAmount(0);
        setTransferReason("");
        setTransferNotes("");
        close();
    };

    const getNewAmount = (plan) => {
        if (!plan) {
            return 0;
        }

        // Secondary -> Primary
        if (transferToPrimary) {
            return Number(plan?.member_price || 0);
        }

        // Primary -> Secondary OR Secondary -> Secondary
        if (selectedTransferMember) {
            return Number(
                selectedTransferMember?.is_dependent
                    ? plan?.dependent_member_price
                    : plan?.non_member_price
            );
        }

        // Existing Primary -> Primary behavior
        return Number(
            data?.primary_eligible
                ? plan?.member_price
                : data?.family_member?.[0]?.is_dependent
                    ? plan?.dependent_member_price
                    : plan?.non_member_price
        );
    };

    const handlePlanSelect = (plan) => {
        setSelectedPlan(plan);

        setTransferAmount(0);

        const newAmount = getNewAmount(plan);

        if (isPartialTransfer) {
            setAdditionalAmount(
                Math.max(newAmount, 0)
            );
            return;
        }

        setAdditionalAmount(
            Math.max(
                newAmount - Number(paidAmount || 0),
                0
            )
        );
    };

    if (!data) {
        return null;
    }

    return (
        <Drawer
            anchor="right"
            open={show}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: {
                        xs: "100%",
                        sm: "70%",
                        md: "70%",
                        lg: "70%",
                    },
                },
            }}
        >
            <Grid
                container
                direction="column"
                width="100%"
                height="100%"
            >
                <Grid
                    container
                    px={1}
                    py={1}
                    borderBottom={1}
                    borderColor="rgba(5, 5, 5, 0.06)"
                >
                    <Grid item alignSelf="center">
                        <IconButtonIcons
                            color="default"
                            title="Close"
                            IconComponent={CloseIcon}
                            onClick={handleClose}
                        />
                    </Grid>

                    <Grid item alignSelf="center">
                        <Typography variant="h6">
                            Transfer Enrolled Activity
                        </Typography>
                    </Grid>
                </Grid>

                <Grid
                    item
                    flex={1}
                    px={3}
                    py={4}
                    overflow="auto"
                >
                    <Typography variant="h6" gutterBottom>
                        Current Enrollment
                    </Typography>

                    <Box
                        sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            mb: 3,
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">
                                    Enrollment ID
                                </Typography>
                                <Typography>
                                    {data?.booking_id || "-"}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">
                                    Member ID
                                </Typography>
                                <Typography>
                                    {data?.member_id?.member_id || "-"}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">
                                    Member Name
                                </Typography>
                                <Typography>
                                    {data?.member_id?.name || "-"}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">
                                    Activity
                                </Typography>
                                <Typography>
                                    {data?.activity_id?.name || "-"}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">
                                    Plan
                                </Typography>
                                <Typography>
                                    {data?.fees_breakup?.plan_name || "-"}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">
                                    Total Amount
                                </Typography>
                                <Typography>
                                    ₹ {Number(data?.total_amount || 0)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">
                                    Already Paid
                                </Typography>
                                <Typography fontWeight="bold">
                                    ₹ {paidAmount}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Typography variant="h6" gutterBottom>
                        Transfer To
                    </Typography>

                    {(data?.primary_eligible === true ||
                        data?.primary_eligible === "true") ? (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{ mb: 2 }}
                            >
                                Select Secondary Member (Optional)
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                            >
                                Leave this unselected to keep the activity with the
                                primary member.
                            </Typography>

                            <Grid container spacing={2}>
                                {(data?.member_id?.family_details || [])
                                    .filter(
                                        (member) =>
                                            member &&
                                            member.active !== false &&
                                            member.active !== "false"
                                    )
                                    .map((member, index) => (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={member?._id || member?.id || index}
                                        >
                                            <Button
                                                fullWidth
                                                variant={
                                                    selectedTransferMember?._id ===
                                                        member?._id
                                                        ? "contained"
                                                        : "outlined"
                                                }
                                                onClick={() => {
                                                    setSelectedTransferMember(member);
                                                    setTransferToPrimary(false);
                                                    setSelectedActivity(null);
                                                    setSelectedBatch(null);
                                                    setSelectedPlan(null);
                                                    setTransferAmount(0);
                                                    setAdditionalAmount(0);
                                                }}
                                            >
                                                <Box width="100%">
                                                    <Typography variant="h6">
                                                        {member?.name || "-"}
                                                    </Typography>

                                                    <Typography variant="subtitle2">
                                                        ID:{" "}
                                                        {member?.id ||
                                                            member?._id ||
                                                            "-"}
                                                    </Typography>
                                                </Box>
                                            </Button>
                                        </Grid>
                                    ))}
                            </Grid>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{ mb: 2 }}
                            >
                                Select Transfer Target
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Button
                                        fullWidth
                                        variant={
                                            transferToPrimary
                                                ? "contained"
                                                : "outlined"
                                        }
                                        onClick={() => {
                                            setTransferToPrimary(true);
                                            setSelectedTransferMember(null);
                                            setSelectedActivity(null);
                                            setSelectedBatch(null);
                                            setSelectedPlan(null);
                                            setTransferAmount(0);
                                            setAdditionalAmount(0);
                                        }}
                                    >
                                        <Box width="100%">
                                            <Typography variant="h6">
                                                Primary Member
                                            </Typography>

                                            <Typography variant="subtitle2">
                                                ID: {data?.member_id?.member_id || "-"}
                                            </Typography>

                                            <Typography variant="body2">
                                                {data?.member_id?.name || "-"}
                                            </Typography>
                                        </Box>
                                    </Button>
                                </Grid>

                                {(data?.member_id?.family_details || [])
                                    .filter(
                                        (member) =>
                                            member &&
                                            member.active !== false &&
                                            member.active !== "false" &&
                                            String(
                                                member?._id || member?.id
                                            ) !==
                                                String(
                                                    data?.family_member?.[0]?._id ||
                                                    data?.family_member?.[0]?.id
                                                )
                                    )
                                    .map((member, index) => (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={member?._id || member?.id || index}
                                        >
                                            <Button
                                                fullWidth
                                                variant={
                                                    !transferToPrimary &&
                                                    selectedTransferMember?._id ===
                                                        member?._id
                                                        ? "contained"
                                                        : "outlined"
                                                }
                                                onClick={() => {
                                                    setTransferToPrimary(false);
                                                    setSelectedTransferMember(member);
                                                    setSelectedActivity(null);
                                                    setSelectedBatch(null);
                                                    setSelectedPlan(null);
                                                    setTransferAmount(0);
                                                    setAdditionalAmount(0);
                                                }}
                                            >
                                                <Box width="100%">
                                                    <Typography variant="h6">
                                                        {member?.name || "-"}
                                                    </Typography>

                                                    <Typography variant="subtitle2">
                                                        ID:{" "}
                                                        {member?.id ||
                                                            member?._id ||
                                                            "-"}
                                                    </Typography>
                                                </Box>
                                            </Button>
                                        </Grid>
                                    ))}
                            </Grid>
                        </Box>
                    )}

                    

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isPartialTransfer}
                                onChange={(e) => {
                                    const partialTransfer = e.target.checked;

                                    setIsPartialTransfer(partialTransfer);
                                    setTransferAmount(0);

                                    if (selectedPlan) {
                                        const newAmount = getNewAmount(selectedPlan);

                                        setAdditionalAmount(
                                            partialTransfer
                                                ? Math.max(newAmount, 0)
                                                : Math.max(
                                                    newAmount -
                                                        Number(paidAmount || 0),
                                                    0
                                                )
                                        );
                                    }
                                }}
                            />
                        }
                        label="Partial / Mid-Plan Transfer"
                    />

                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <AutoCompleteServerSide
                                label="Select Activity *"
                                name="transfer_activity"
                                id="transfer_activity"
                                fullWidth
                                fetchDataFunction={(params) =>
                                    getActivityList(params)
                                }
                                onChange={(value) => {
                                    setSelectedActivity(value);
                                    setSelectedBatch(null);
                                    setSelectedPlan(null);
                                    setTransferAmount(0);
                                    setAdditionalAmount(0);
                                }}
                                defaultValue={null}
                                apiParams={{
                                    active: true,
                                    batch_status: true,
                                    type: "enrollment",
                                }}
                            />
                        </Grid>

                        {selectedActivity?.batch_data?.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Select Batch *
                                </Typography>

                                <Grid container spacing={2}>
                                    {selectedActivity.batch_data.map(
                                        (batch, index) => {
                                            if (
                                                batch?.type !== "enrollment" ||
                                                !batch?.status
                                            ) {
                                                return null;
                                            }

                                            return (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={6}
                                                    md={4}
                                                    key={
                                                        batch?._id || index
                                                    }
                                                >
                                                    <Button
                                                        fullWidth
                                                        disabled={
                                                            Number(
                                                                batch?.batch_limit ||
                                                                    0
                                                            ) <= 0
                                                        }
                                                        variant={
                                                            selectedBatch?._id ===
                                                            batch?._id
                                                                ? "contained"
                                                                : "outlined"
                                                        }
                                                        onClick={() => {
                                                            setSelectedBatch(batch);
                                                            setSelectedPlan(null);
                                                            setTransferAmount(0);
                                                            setAdditionalAmount(0);
                                                        }}
                                                    >
                                                        {batch?.batch_name}
                                                    </Button>
                                                </Grid>
                                            );
                                        }
                                    )}
                                </Grid>
                            </Grid>
                        )}

                        {selectedBatch?.fees?.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Select Plan *
                                </Typography>

                                <Grid container spacing={2}>
                                    {selectedBatch.fees.map((plan) => {
                                        const newAmount =
                                            getNewAmount(plan);

                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                md={4}
                                                key={plan?.plan_id}
                                            >
                                                <Button
                                                    fullWidth
                                                    variant={
                                                        selectedPlan?.plan_id ===
                                                        plan?.plan_id
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    onClick={() =>
                                                        handlePlanSelect(plan)
                                                    }
                                                >
                                                    <Box width="100%">
                                                        <Typography
                                                            variant="h6"
                                                        >
                                                            {
                                                                plan?.plan_name
                                                            }
                                                        </Typography>

                                                        <Typography variant="subtitle2">
                                                            Amount: ₹
                                                            {newAmount}
                                                        </Typography>
                                                    </Box>
                                                </Button>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        )}

                        {selectedPlan && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ mb: 2 }}
                                    >
                                        Payment Summary
                                    </Typography>

                                    <Grid container spacing={1}>
                                        <Grid item xs={8}>
                                            <Typography>
                                                New Activity Amount
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={4}>
                                            <Typography textAlign="right">
                                                ₹{" "}
                                                {getNewAmount(
                                                    selectedPlan
                                                )}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={8}>
                                            <Typography>
                                                Already Paid
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={4}>
                                            <Typography textAlign="right">
                                                ₹ {paidAmount}
                                            </Typography>
                                        </Grid>

                                        {isPartialTransfer && (
                                            <>
                                                <Grid item xs={8}>
                                                    {/* <Typography>
                                                        Transfer Amount
                                                    </Typography> */}
                                                    <Typography>
                                                        Amount Utilized by Member
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        value={transferAmount}
                                                        onChange={(e) => {
                                                            const amount = Math.max(
                                                                Number(e.target.value || 0),
                                                                0
                                                            );

                                                            setTransferAmount(amount);

                                                            const newAmount = getNewAmount(selectedPlan);

                                                            setAdditionalAmount(
                                                                Math.max(newAmount - amount, 0)
                                                            );
                                                        }}
                                                        inputProps={{
                                                            min: 0,
                                                        }}
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 1 }} />
                                        </Grid>

                                        <Grid item xs={8}>
                                            <Typography fontWeight="bold">
                                                Additional Amount Payable
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={4}>
                                            <Typography
                                                textAlign="right"
                                                fontWeight="bold"
                                            >
                                                ₹ {additionalAmount}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Reason for Transfer"
                                                value={transferReason}
                                                onChange={(e) =>
                                                    setTransferReason(
                                                        e.target.value
                                                    )
                                                }
                                                multiline
                                                minRows={2}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Additional Notes"
                                                value={transferNotes}
                                                onChange={(e) =>
                                                    setTransferNotes(
                                                        e.target.value
                                                    )
                                                }
                                                multiline
                                                minRows={2}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                <Grid
                    item
                    flexShrink={0}
                    borderTop={1}
                    borderColor="divider"
                    sx={{ p: 1 }}
                >
                    <Grid
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                        }}
                    >
                        <Button
                            color="warning"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>

                        <Button
                            disabled={
                                transferLoading ||
                                !selectedActivity ||
                                !selectedBatch ||
                                !selectedPlan ||
                                !transferReason.trim()
                            }
                            onClick={async () => {
                                try {
                                    const newAmount = getNewAmount(selectedPlan);

                                    const calculatedAdditionalAmount = isPartialTransfer
                                        ? Math.max(
                                            newAmount - Number(transferAmount || 0),
                                            0
                                        )
                                        : Math.max(
                                            newAmount - Number(paidAmount || 0),
                                            0
                                        );

                                    const payload = {
                                        booking_id: data?._id,
                                        activity_id: selectedActivity?._id,
                                        batch: selectedBatch?._id,
                                        fees_breakup: selectedPlan,
                                        total_amount: newAmount,
                                        additional_amount: calculatedAdditionalAmount,
                                        reason: transferReason.trim(),
                                        notes: transferNotes.trim(),

                                        ...(selectedTransferMember && {
                                            target_family_member_id:
                                                selectedTransferMember?._id ||
                                                selectedTransferMember?.id,
                                        }),

                                        transfer_to_primary: transferToPrimary,

                                        ...(isPartialTransfer && {
                                            transfer_amount: Number(
                                                transferAmount || 0
                                            ),
                                        }),
                                    };

                                    console.log("TRANSFER PAYLOAD:", payload);

                                    const response = await transferBooking(payload).unwrap();

                                    dispatch(
                                        setSnackBar({
                                            open: true,
                                            message:
                                                response?.message ||
                                                "Enrollment transferred successfully",
                                            severity: "success",
                                        })
                                    );

                                    handleClose();
                                } catch (error) {
                                    console.error("TRANSFER ERROR:", error);

                                    dispatch(
                                        setSnackBar({
                                            open: true,
                                            message:
                                                error?.data?.message ||
                                                error?.message ||
                                                "Failed to transfer enrollment",
                                            severity: "error",
                                        })
                                    );
                                }
                            }}
                        >
                            {transferLoading ? "Transferring..." : "Transfer"}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Drawer>
    );
};